import type { Collector } from "./types";
import { isBrowser } from "./types";
import { getGPUNormalizedDisplay } from "./webgl";

export interface DisplayInfo {
  gamut: "srgb" | "p3" | "rec2020";
  hdr: boolean;
}

export const displayCollector: Collector<DisplayInfo> = {
  name: "display",
  version: 1,
  timeoutMs: 200,
  collect() {
    return getDisplayInfo();
  },
};

function getDisplayInfo(): DisplayInfo {
  if (!isBrowser) return { gamut: "srgb", hdr: false };
  const hasMM = typeof window.matchMedia === "function";
  const mm = hasMM ? window.matchMedia.bind(window) : null;

  let gamut: "srgb" | "p3" | "rec2020" = "srgb";
  if (mm) {
    if (mm("(color-gamut: rec2020)").matches) gamut = "rec2020";
    else if (mm("(color-gamut: p3)").matches) gamut = "p3";
  }

  const hdr = mm ? mm("(dynamic-range: high)").matches : false;
  return { gamut, hdr };
}

export interface StableDisplay {
  primaryW: number | null;
  primaryH: number | null;
  dpr: number | null;
  depth: number | null;
  source: string;
  maxTexture?: number;
  gpuRenderer?: string;
  gpuVendor?: string;
  displayCount?: number;
  maxResolution?: number;
}

export const stableDisplayCollector: Collector<StableDisplay> = {
  name: "stableDisplay",
  version: 1,
  timeoutMs: 1000,
  async collect() {
    return await getStableDisplayInfo();
  },
};

/**
 * Display-independent screen fingerprinting for external monitor consistency.
 * Strategy preference: Screen Management API → GPU normalization → null fallback.
 *
 * v5 (2026-05-04): the previous "traditional-stable" path emitted screen.width/
 * height rounded to 100, which DevTools emulation could shift. v5 returns null
 * primaryW/H for that path so the server's v5 hash isn't perturbed by
 * emulation. Source string stays `"traditional-stable"` for diagnostic
 * continuity.
 */
async function getStableDisplayInfo(): Promise<StableDisplay> {
  if (!isBrowser) {
    return {
      primaryW: null,
      primaryH: null,
      dpr: null,
      depth: null,
      source: "ssr",
    };
  }

  if ("getScreenDetails" in window) {
    try {
      const permission = await navigator.permissions.query({ name: "window-management" as any });
      if (permission.state === "granted") {
        const screens = await (window as any).getScreenDetails();
        const primaryScreen =
          screens.screens.find((s: any) => s.isPrimary) ||
          screens.screens.reduce((max: any, curr: any) =>
            curr.width * curr.height > max.width * max.height ? curr : max,
          );

        return {
          primaryW: primaryScreen.width,
          primaryH: primaryScreen.height,
          dpr: Math.round(primaryScreen.devicePixelRatio * 100) / 100,
          depth: primaryScreen.colorDepth,
          displayCount: screens.screens.length,
          source: "screen-management-api",
        };
      }
    } catch {
      /* fallback to next strategy */
    }
  }

  const gpuInfo = getGPUNormalizedDisplay();
  if (gpuInfo.isNormalized) {
    return {
      primaryW: gpuInfo.primaryW ?? null,
      primaryH: gpuInfo.primaryH ?? null,
      dpr: gpuInfo.dpr ?? null,
      depth: gpuInfo.depth ?? null,
      maxTexture: gpuInfo.maxTexture,
      gpuRenderer: gpuInfo.gpuRenderer,
      gpuVendor: gpuInfo.gpuVendor,
      source: "gpu-normalized",
    };
  }

  // v5: no raw screen.width/height fallback — those drift under DevTools
  // emulation. Server v5 reads null and skips the screen field in the hash.
  return {
    primaryW: null,
    primaryH: null,
    dpr: null,
    depth: null,
    source: "traditional-stable",
  };
}
